/**
 * lib/offlinePackage.js — FP-037 (2026-09-09 audit remediation).
 *
 * The explicit "download this project for offline use" package
 * contract. Previously offline availability was purely incidental —
 * whatever a field user happened to browse online first got cached by
 * the service worker. There was no way to know ahead of time what a
 * download covers, how much space it needs, whether it fully
 * succeeded, or whether it has gone stale since.
 *
 * This module does NOT reimplement caching — every URL fetched here
 * flows through the SAME service-worker fetch handler that already
 * owns cache-first/network-first policy per resource type (see
 * service-worker.js). This module's job is purely to:
 *   1. Know the COMPLETE list of URLs a project needs (via the
 *      backend's /offline_manifest, including linked photos, which
 *      the manifest alone doesn't enumerate — this module fetches the
 *      per-sheet photo list to get individual photo URLs).
 *   2. Report a storage estimate against real device quota BEFORE
 *      starting.
 *   3. Track progress and PERSIST it, so an interrupted download
 *      (browser closed, tab navigated away, connection lost) resumes
 *      from where it left off instead of restarting or silently
 *      claiming completeness it never reached.
 *   4. Report an HONEST completeness/freshness state — never claim
 *      "available offline" when some items failed or the live project
 *      has moved on since the download.
 */
import { api, getBearer, API_BASE } from "@/lib/api";

const STATE_KEY_PREFIX = "fop_offline_pkg_";

function _stateKey(projectId) {
  return `${STATE_KEY_PREFIX}${projectId}`;
}

function _loadState(projectId) {
  try {
    const raw = window.localStorage.getItem(_stateKey(projectId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function _saveState(projectId, state) {
  try {
    window.localStorage.setItem(_stateKey(projectId), JSON.stringify(state));
    return true;
  } catch {
    return false; // quota exceeded or unavailable — caller must surface this
  }
}

/** Real device storage quota vs already-used, so the download UI can
 * warn BEFORE starting rather than fail silently partway through. */
export async function getStorageEstimate() {
  if (typeof navigator === "undefined" || !navigator.storage || !navigator.storage.estimate) {
    return { supported: false };
  }
  try {
    const { usage, quota } = await navigator.storage.estimate();
    return { supported: true, usageBytes: usage || 0, quotaBytes: quota || 0 };
  } catch {
    return { supported: false };
  }
}

async function _fetchManifest(projectId) {
  const { data } = await api.get(`/projects/${projectId}/offline_manifest`);
  return data;
}

/** Enumerate the individual photo file/thumb URLs for a sheet — the
 * manifest only carries counts (it would be too expensive to embed
 * every photo's URL in a project-wide manifest response), so the
 * downloader resolves the real list per sheet at download time.
 *
 * GH-02 (real-plan certification, 2026-09-30) — if THIS enumeration
 * call itself failed (not an individual photo fetch — the LIST call
 * that discovers which photos exist), the previous code swallowed the
 * error and returned an empty array. Those photo URLs then never
 * entered `urls` at all, so `downloadForOffline` never attempted them
 * and never counted them as failed either — the package silently
 * omitted every photo on that sheet while still reporting 0 failures
 * and "available". Returning `{ urls: [], failed: true }` lets the
 * caller record THIS failure explicitly (see `_resolveFullUrlList`),
 * so a required record's absence is always visible, never invisible.
 */
async function _sheetPhotoUrls(sheetId) {
  try {
    const { data } = await api.get(`/sheets/${sheetId}/photos`);
    const photos = Array.isArray(data) ? data : (data.photos || []);
    const urls = [];
    for (const p of photos) {
      if (p.id) {
        urls.push(`/api/photos/${p.id}/file`, `/api/photos/${p.id}/thumb`);
      }
    }
    return { urls, failed: false };
  } catch {
    return { urls: [], failed: true };
  }
}

/** Build the complete, flat URL list for a manifest — sheet-level URLs
 * plus every linked photo discovered per sheet. This is the actual
 * "required drawing content and supported linked records" the Founder
 * asked for, not just drawings alone.
 *
 * GH-06 (real-plan certification, 2026-09-30) — the manifest's raster
 * URL is bare (`/api/sheets/<id>/raster`, dpr=1 implied). The Sheet
 * Viewer's actual <img> request (`lib/api.js::sheetRasterUrl`) asks for
 * `?dpr=2` on any device with `devicePixelRatio >= 1.5` — every iPad.
 * Downloading only the dpr=1 variant left the retina image genuinely
 * absent from cache even once the auth-identity/cache-key mismatch
 * (also GH-06, fixed in service-worker.js) was corrected — a true
 * network miss offline, not a bug in matching an entry that WAS there.
 * Fetch both variants so either DPR the field device actually requests
 * is already warm.
 *
 * Returns `{ urls, preFailedUrls }` — `preFailedUrls` (GH-02) are
 * enumeration endpoints that themselves failed and must be surfaced as
 * real failures, not silently dropped. */
async function _resolveFullUrlList(manifest) {
  const urls = [];
  const preFailedUrls = [];
  // GH-09 — project-scoped first-open dependencies, fetched once (not
  // once per sheet) regardless of which sheet is opened first offline.
  for (const u of manifest.project_urls || []) {
    urls.push(u);
  }
  for (const sheet of manifest.sheets) {
    for (const u of sheet.urls) {
      urls.push(u);
      if (/\/raster$/.test(u)) urls.push(`${u}?dpr=2`);
    }
    const photoResult = await _sheetPhotoUrls(sheet.id);
    if (photoResult.failed) {
      preFailedUrls.push(`/api/sheets/${sheet.id}/photos`);
    } else {
      urls.push(...photoResult.urls);
    }
  }
  return { urls, preFailedUrls };
}

/** GH-04/GH-06 (real-plan certification, 2026-09-30) — tell the service
 * worker which bucket (derived from this SAME bearer token, see
 * service-worker.js::identityForBearer) a set of URLs belongs to, so it
 * can pin them against its own eviction (GH-04) or release that promise
 * when a package is cleared. A missing/uncontrolled service worker
 * (not yet activated, or offline-package features used from a browser
 * tab the SW hasn't claimed yet) is a no-op, not an error — pinning is
 * a best-effort durability improvement on top of the browser's own
 * cache, never a requirement for the download itself to succeed. */
function _postToServiceWorker(type, urls) {
  if (typeof navigator === "undefined" || !navigator.serviceWorker || !navigator.serviceWorker.controller) {
    return;
  }
  if (!urls || !urls.length) return;
  navigator.serviceWorker.controller.postMessage({ type, token: getBearer(), urls });
}

/** GH-01 (real-plan certification, 2026-09-30) — ask the service worker
 * to prove, against its OWN durable Cache Storage (not this module's
 * localStorage bookkeeping), which of these URLs are actually present
 * right now. Returns `null` (not an empty result) when there is no
 * controlling service worker to ask — the caller must not interpret
 * "couldn't verify" as "verified absent", nor as "verified present".
 */
function _verifyDurableCache(urls, timeoutMs = 4000) {
  if (typeof navigator === "undefined" || !navigator.serviceWorker || !navigator.serviceWorker.controller) {
    return Promise.resolve(null);
  }
  if (!urls || !urls.length) return Promise.resolve({ present: [], missing: [] });
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    // B3-03 (2026-10-01 completion directive): every completion path
    // (message received, timeout, postMessage throwing) must close
    // port1 — an open MessagePort keeps a Node worker_threads
    // MessageChannel (used by the jest test harness for this helper)
    // alive indefinitely, which previously left the test process
    // hanging after all assertions passed. port2 was transferred to
    // the service worker via postMessage, so only port1 is ours to
    // close here.
    const finish = (value) => {
      clearTimeout(timer);
      channel.port1.close();
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    channel.port1.onmessage = (ev) => finish(ev.data);
    try {
      navigator.serviceWorker.controller.postMessage(
        { type: "CHECK_CACHED", token: getBearer(), urls },
        [channel.port2],
      );
    } catch {
      finish(null);
    }
  });
}

/**
 * Kick off (or resume) a download. `onProgress({done, total, failed})`
 * is called after every item. Returns the final state.
 *
 * Interruption recovery: progress is persisted to localStorage after
 * EVERY item (not just at the end), so if the browser is closed or the
 * tab navigates away mid-download, calling this again for the same
 * project resumes from the exact item it stopped at — completed URLs
 * are never re-fetched, and previously failed URLs are retried.
 */
export async function downloadForOffline(projectId, onProgress) {
  const manifest = await _fetchManifest(projectId);
  const { urls, preFailedUrls } = await _resolveFullUrlList(manifest);

  let state = _loadState(projectId);
  // A stale in-progress download from a DIFFERENT manifest_version
  // (the project changed since the last attempt) must not silently
  // resume as if nothing changed — start fresh against the new list.
  if (!state || state.manifest_version !== manifest.manifest_version) {
    state = {
      manifest_version: manifest.manifest_version,
      project_name: manifest.project_name,
      total: urls.length,
      done_urls: [],
      failed_urls: [],
      started_at: new Date().toISOString(),
      completed_at: null,
    };
  } else {
    // Resume: keep prior done_urls, but total reflects the CURRENT
    // (possibly changed) list length.
    state.total = urls.length;
  }
  state.completed_at = null;
  // GH-02 — surface enumeration failures as real failures immediately,
  // not silent omissions. A retry of the whole download re-attempts the
  // enumeration call for exactly this sheet (it isn't in `done_urls`).
  // Reconcile first: a sheet whose enumeration failed on a PRIOR attempt
  // but now succeeds (preFailedUrls no longer contains its marker) must
  // have that stale marker removed — otherwise a fully successful retry
  // keeps reporting "incomplete" forever, because nothing in the main
  // fetch loop below ever re-visits a synthetic enumeration marker.
  const enumerationMarker = /^\/api\/sheets\/[^/]+\/photos$/;
  state.failed_urls = state.failed_urls.filter(
    (f) => !enumerationMarker.test(f) || preFailedUrls.includes(f),
  );
  for (const u of preFailedUrls) {
    if (!state.failed_urls.includes(u)) state.failed_urls.push(u);
  }
  const doneSet = new Set(state.done_urls);
  // R-03 (independent recheck, 2026-10-01) — `done_urls` is this
  // module's OWN bookkeeping of what fetched successfully at some
  // point in the past; it is not proof anything is still actually in
  // the service worker's durable cache today. A retry/resume used to
  // trust it blindly — a resource genuinely evicted since the last
  // successful download (normal LRU pressure from unrelated browsing,
  // or a cleared cache) was silently skipped forever, because it was
  // never "remaining" in the first place. `_verifyDurableCache` (used
  // by `getPackageStatus` to detect this exact "evicted" state) is now
  // ALSO consulted here, before deciding what to (re)fetch, so the one
  // control the operator actually has — press Download/retry — is the
  // one that repairs it, not just reports it.
  const verified = await _verifyDurableCache(state.done_urls);
  if (verified && Array.isArray(verified.missing) && verified.missing.length) {
    for (const u of verified.missing) {
      doneSet.delete(u);
      state.done_urls = state.done_urls.filter((d) => d !== u);
    }
  }
  const remaining = urls.filter((u) => !doneSet.has(u));

  for (const url of remaining) {
    try {
      // FP-037 bug fix (testing_agent_v3_fork wave-b-turn2) — this app
      // authenticates via a Bearer token in localStorage (api.js), NOT
      // cookies; `credentials: "include"` alone sends no auth at all,
      // so every asset fetch 401'd and the download always failed
      // 100% of the time. Attach the same bearer token the axios `api`
      // instance uses.
      const token = getBearer();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(url, { credentials: "include", headers });
      if (res && res.ok) {
        state.done_urls.push(url);
        state.failed_urls = state.failed_urls.filter((f) => f !== url);
        // GH-04 — pin this resource against the service worker's own
        // cache eviction for as long as this package is promised.
        // Fire-and-forget per item (cheap, idempotent) rather than one
        // call at the very end, so a download interrupted partway still
        // pins everything it actually finished.
        _postToServiceWorker("PIN_URLS", [url]);
      } else {
        if (!state.failed_urls.includes(url)) state.failed_urls.push(url);
      }
    } catch {
      if (!state.failed_urls.includes(url)) state.failed_urls.push(url);
    }
    const persisted = _saveState(projectId, state);
    onProgress && onProgress({
      done: state.done_urls.length, total: state.total,
      failed: state.failed_urls.length, persisted,
    });
  }

  if (state.failed_urls.length === 0) {
    state.completed_at = new Date().toISOString();
  }
  _saveState(projectId, state);
  return state;
}

/**
 * Honest status for the UI: NEVER report "fully available offline"
 * unless every item actually succeeded AND the manifest_version still
 * matches the live project (no revision/record-count drift since the
 * download) AND (GH-01, real-plan certification, 2026-09-30) every
 * "done" URL is independently confirmed to be ACTUALLY present in the
 * service worker's durable Cache Storage right now — not merely
 * recorded once in this module's own localStorage bookkeeping, which
 * cannot see the service worker's own eviction (GH-04), a
 * quota-exceeded write that silently failed, or an interrupted
 * download that never reached the final "all done" write at all
 * (reproduced: `done=1, total=7, completed_at=null, failed_urls=[]`
 * was reported "available" before this fix — bookkeeping alone can
 * never distinguish "interrupted after 1 of 7" from "genuinely done").
 */
export async function getPackageStatus(projectId) {
  const local = _loadState(projectId);
  if (!local) return { state: "not_downloaded" };

  // GH-01 — an interrupted download (closed mid-fetch, before the loop
  // even reached every URL) has `failed_urls=[]` (nothing was ever
  // attempted AND failed) but `done_urls.length < total`. The previous
  // code only ever checked `failed_urls.length`, so this case fell
  // straight through to "available".
  const incompleteCount = local.done_urls.length < local.total;

  let liveManifest = null;
  try {
    liveManifest = await _fetchManifest(projectId);
  } catch {
    // Can't reach the network right now — report what we know locally
    // without claiming freshness we can't verify. An interrupted
    // download is still reported as interrupted even offline.
    if (incompleteCount) {
      return { state: "interrupted", total: local.total, done: local.done_urls.length,
               failed: local.failed_urls.length, downloadedAt: local.completed_at };
    }
    return {
      state: local.failed_urls.length > 0 ? "incomplete" : "downloaded_freshness_unknown",
      total: local.total, done: local.done_urls.length,
      failed: local.failed_urls.length, downloadedAt: local.completed_at,
    };
  }
  const stale = liveManifest.manifest_version !== local.manifest_version;
  if (incompleteCount) {
    return { state: "interrupted", total: local.total, done: local.done_urls.length,
             failed: local.failed_urls.length, stale, downloadedAt: local.completed_at };
  }
  if (local.failed_urls.length > 0) {
    return { state: "incomplete", total: local.total, done: local.done_urls.length,
             failed: local.failed_urls.length, stale, downloadedAt: local.completed_at };
  }
  if (stale) {
    return { state: "stale", total: local.total, done: local.done_urls.length,
             downloadedAt: local.completed_at };
  }
  // Bookkeeping says every URL succeeded and the manifest hasn't
  // drifted. Before calling this "available", independently verify
  // against the service worker's actual Cache Storage contents.
  const verification = await _verifyDurableCache(local.done_urls);
  if (verification === null) {
    // No controlling service worker to ask (e.g. first load before SW
    // activation, or a browser context without SW support). We cannot
    // prove durable presence either way — say so honestly rather than
    // claiming "available" on bookkeeping alone.
    return { state: "downloaded_unverified", total: local.total, done: local.done_urls.length,
             downloadedAt: local.completed_at };
  }
  if (verification.missing.length > 0) {
    return { state: "evicted", total: local.total, done: local.done_urls.length,
             missingFromCache: verification.missing.length, downloadedAt: local.completed_at };
  }
  return { state: "available", total: local.total, done: local.done_urls.length,
           downloadedAt: local.completed_at };
}

function _allPackageProjectIds() {
  const ids = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STATE_KEY_PREFIX)) ids.push(key.slice(STATE_KEY_PREFIX.length));
    }
  } catch {}
  return ids;
}

/** R-01 (independent recheck, 2026-10-01) — `project_urls` is not
 * exclusively per-project: `/api/users` (tenant-wide) is included in
 * EVERY project's manifest (see `routers/projects.py::offline_manifest`),
 * so two downloaded packages on the same device genuinely share it.
 * `clearPackage` used to unpin every one of ITS OWN `done_urls`
 * unconditionally — if a second package still downloaded was relying
 * on that same shared URL staying pinned, clearing the FIRST package
 * silently stripped the second package's protection too, even though
 * that package was never cleared. Collect every OTHER still-tracked
 * package's `done_urls` first, and never unpin anything still claimed
 * by one of them. */
function _urlsStillClaimedByOtherPackages(excludeProjectId) {
  const claimed = new Set();
  for (const pid of _allPackageProjectIds()) {
    if (pid === excludeProjectId) continue;
    const other = _loadState(pid);
    if (other && Array.isArray(other.done_urls)) {
      for (const u of other.done_urls) claimed.add(u);
    }
  }
  return claimed;
}

export function clearPackage(projectId) {
  // GH-04 — release this package's pin promise before discarding our
  // own bookkeeping, so the service worker's ordinary eviction policy
  // can reclaim the space again. R-01 — EXCEPT for a URL another
  // still-tracked package also depends on; that one stays pinned.
  const local = _loadState(projectId);
  if (local && local.done_urls && local.done_urls.length) {
    const stillClaimed = _urlsStillClaimedByOtherPackages(projectId);
    const toUnpin = local.done_urls.filter((u) => !stillClaimed.has(u));
    if (toUnpin.length) _postToServiceWorker("UNPIN_URLS", toUnpin);
  }
  try { window.localStorage.removeItem(_stateKey(projectId)); } catch {}
}
